/**
 * ONE TAP — ARENA: "SKYLINE NINE"
 *
 * An open rooftop complex on top of five buildings, under a bare sky. There is no
 * ceiling on this map. Every primary fighting space is a ROOF: sunlit white concrete,
 * parapets, water tanks, plant masses, ducting, skylights, a helipad. The ONLY roofed
 * volumes in the whole arena are the four glass link bridges — fourteen metres of
 * traversal each, glass on all six faces, both ends open, and the exit visible from
 * the moment you step in. Nothing else on this map has a lid. `verifyOpenSky()`
 * proves it at load, and proves separately that both spawn decks are open sky end to
 * end rather than at three sampled points.
 *
 * Verticality is stacked ROOFS, not stacked decks in a box: 4m to 34m in six discrete
 * levels, with real air between the buildings. Falling between towers is the void —
 * the shafts drop to y = -46 and the kill plane sits at y = -24, so a missed jump is
 * a 28 m fall past a building face and then nothing.
 *
 * Mirrored across Z. Team A holds -Z ("SOUTH"), team B holds +Z ("NORTH").
 *
 * ---------------------------------------------------------------------------
 * SECTION, looking east. Heights in metres.
 * ---------------------------------------------------------------------------
 *
 *   y=55                                  * MAST TIP (the top anchor)
 *   y=34                              [ THE PAD ]                      helipad
 *   y=31   [CRWN|CRWN]             /  chutes   \              [CRWN|CRWN]
 *   y=26   [======= SOUTH ROOF ======]             [====== NORTH ROOF ======]
 *              \ roof ramp                                 roof ramp /
 *   y=20        [== SOUTH DECK ==]--gantry--[MID RING]--gantry--[== NORTH DECK ==]
 *   y=12   [STACK]                    \  slip ramps  /                  [STACK]
 *   y= 4       [=== TERRACE ===]--bridge--[ APRON ]--bridge--[=== TERRACE ===]
 *              |                |         |       |          |              |
 *   y=-24  - - - - - - - - - - K I L L   P L A N E - - - - - - - - - - - - - -
 *   y=-46      |________________|         |_______|          |______________|
 *
 * ---------------------------------------------------------------------------
 * PLAN at deck level. +X east, +Z north. (Mirror axis is the z = 0 line.)
 * ---------------------------------------------------------------------------
 *
 *        -67    -47   -33 -23 -13  0  13 23  33   47    67
 *  +84    .      .     +-----------------------+     .      .
 *         .      .     |  s  NORTH ROOF y26 s  |     .      .   s = spawn
 *  +74    .   +--------| [PLANT] | [PLANT] |--------+   .   | = LIGHT COURT
 *         .   |  WEST  |          s            |  EAST  |   .
 *  +54    .   |  STACK +-----------------------+  STACK |   .
 *         .   |  y12   |  [ N BILLBOARD ]      |  y12   |   .
 *  +50    .   +--------+---------------------- +--------+   .
 *         .            |     NORTH DECK   y20  |            .
 *  +34    .   +--------+--[G]-----------[G]----+--------+   .
 *         .   |        |     MID RING     y20  |        |   .
 *  +23    .   |  WEST  +----+-------------+----+  EAST  |   .
 *  +13    .   | TERRACE|    |  THE PAD    |    | TERRACE|   .
 *   0     .   |   y4   |====|    y34      |====|   y4   |   .   <-- PIPE BRIDGES
 *  -13    .   |        |    |  + MAST     |    |        |   .
 *  -23    .   |        +----+-------------+----+        |   .
 *         .   |        |     MID RING     y20  |        |   .
 *  -34    .   +--------+--[G]-----------[G]----+--------+   .
 *         .            |     SOUTH DECK   y20  |            .
 *  -50    .   +--------+-----------------------+--------+   .
 *         .   |  WEST  |  [ S BILLBOARD ]      |  EAST  |   .
 *  -54    .   |  STACK +-----------------------+  STACK |   .
 *         .   |  y12   |          s            |  y12   |   .
 *  -74    .   +--------| [PLANT] | [PLANT] |--------+   .
 *         .      .     |  s  SOUTH ROOF y26 s  |     .      .
 *  -84    .      .     +-----------------------+     .      .
 *
 * ---------------------------------------------------------------------------
 * CALLOUTS
 * ---------------------------------------------------------------------------
 *   THE PAD        y34  the helipad on top of the core tower. Highest walkable
 *                       surface on the map and the only one that can see both
 *                       teams' roofs. 26 x 26 m, one antenna MAST for cover, no
 *                       parapet. Eight CHUTE ramps fall off it in four directions.
 *   THE MAST            the antenna on the pad, y34 -> y52. It is the tallest thing
 *                       in the arena, the anchor everyone can see, and the reason
 *                       the two CROWNs cannot snipe each other across the map.
 *   MID RING       y20  the 10 m walkway around the base of the pad. Level with both
 *                       decks. This is the map's crossroads and its longest duel.
 *   THE APRON      y4   the ring around the base of the whole core tower, 16 m below
 *                       MID RING. Standing on THE PAD you cannot see one square metre
 *                       of it — the core's own shoulder hides it. This is the flank
 *                       that beats the pad, and every fight for the middle is really
 *                       a fight about who is on the apron.
 *   SLIP           the two long 32 deg ramps from THE APRON up to MID RING, hugging
 *                  the outer edge of the apron ring, meeting at a LANDING at z=0.
 *   CHUTES         the eight 32 deg ramps between MID RING and THE PAD.
 *   SOUTH/NORTH DECK  y20  each team's forward roof. Skylights, planters, ducting.
 *                  Two GANTRIES lead from it onto MID RING, and they are in the
 *                  pad's shadow, so crossing one is safe from the long lane and
 *                  totally exposed from above and below.
 *   BILLBOARD      the 13 m rooftop sign standing on the back of each deck. It is
 *                  what stops a spawn from ever seeing the far spawn, it is 20 m of
 *                  wall-run, and it is the loudest piece of colour on the map.
 *   SOUTH/NORTH ROOF  y26  the home roof. Water tanks, ducting, window-washing rigs.
 *   PLANT MASS     the pair of solid 6 x 14 x 5 m blocks at the back of each home
 *                  roof. Not a room — there is nothing inside them. All four faces
 *                  are wall-run, their top face is the CROWN, and the CROWN RAMP runs
 *                  up the front of each one.
 *   LIGHT COURT    the 8 m open-air slot between the two plant masses, at home-roof
 *                  level, open at both z ends and open to the sky for its whole
 *                  length. Walls 5 m high either side, the court mast up the middle,
 *                  and the team's gun spawns at its mouth. This is the space that
 *                  used to be a roofed plant room, and it is the single biggest
 *                  change between "rooftop map with an interior behind the spawn" and
 *                  "rooftop map".
 *   CROWN          y31  the plant-mass roof and the highest point on a team's side.
 *                  Sees its own half of the map and the pad. Cannot see the enemy
 *                  crown — the pad blocks it. Two 6 m walkable strips either side of
 *                  the LIGHT COURT, joined at the back by the CROWN CATWALK, with a
 *                  clipped bed on each for cover.
 *   WEST/EAST STACK  y12  the corner blocks that bridge home roof to terrace. One
 *                  32 m ramp arrives from the roof, one leaves for the terrace.
 *   WEST/EAST TERRACE  y4  the long low service roofs down each flank. Ducting,
 *                  vents, tanks, and a 20 m STAIR CORE with a perch on top. The
 *                  lowest, safest, slowest route to the middle.
 *   PIPE BRIDGE    the open grate crossing at z=0 over each canyon. 14 m of nothing
 *                  underneath and no cover on it at all.
 *   GLASS LINK     the enclosed crossing at |z|=24 over each canyon. Glass floor,
 *                  glass walls, glass roof, both ends open. The other way across, and
 *                  the only lid left in the arena — one you see the sky through.
 *   THE CANYON     the 14 m slot between an APRON edge and a TERRACE edge, and the
 *                  27 m slot between a DECK edge and a TERRACE edge. Both drop 28 m
 *                  to the kill plane. Facade bands on the canyon walls are
 *                  wall-runnable: a missed jump is recoverable if you are good.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS SHAPED LIKE THIS
 * ---------------------------------------------------------------------------
 *   - NO ROOF OVER A FIGHT. Every fighting surface is the top face of a solid block,
 *     so the sky is the ceiling everywhere. `verifyOpenSky()` proves it rather than
 *     asserting it: on a 3 m grid, 0.4% of standable cells have anything opaque above
 *     them at any height, against a hard budget of 1% — six cells, all of them the
 *     strip of apron under the LANDING. It was 5.4% while the plant rooms had lids on.
 *     Measured independently with real collision raycasts on a 2 m grid: 91.5% of
 *     standable ground has open sky DIRECTLY overhead, 96.7% can see sky within
 *     22 degrees of straight up, and there is nowhere at all on this map you can stand
 *     and see no sky. What is left of the 3.3% is ramp soffit — the ring under a
 *     CHUTE, the apron under a SLIP — which is a bridge over your head, open on both
 *     long sides, and not the thing this rule is about.
 *   - THE SPAWN DECK IS THE ONE THAT MATTERS MOST. Every player spends the first
 *     seconds of every round there, so it sets the feel of the arena out of all
 *     proportion to its size, and it is checked at every grid cell of both home roofs
 *     rather than at the three spawn points.
 *   - NO SPAWN SEES A SPAWN. The pad block occludes |x| <= 13 and each BILLBOARD
 *     occludes 13.5 <= |x| <= 20; the home roofs are exactly 40 m wide, so the union
 *     covers every possible spawn-to-spawn line but a half-metre slot. Proven
 *     numerically in `verifySightlines()`.
 *   - THE HIGH GROUND IS BLIND UNDERNEATH IT. The pad is 14 m above MID RING and MID
 *     RING is 16 m above THE APRON, and each step is inset, so holding a level means
 *     giving up all sight of the level below. There is no stance on this map that
 *     sees the map.
 *   - PARAPETS EVERYWHERE. 1.15 m waist walls line every roof edge in gapped
 *     segments: standing you can trade, crouching you are gone, and the gaps are
 *     where you run straight off into the air. In a game where the head is an
 *     instant kill this is the most valuable cover there is, so it is also the most
 *     common object in the level.
 *   - THE FOUR VERBS. Slide/surf: 28 ramps at 18-33 deg forming a continuous descent
 *     from THE PAD to THE APRON and from CROWN to TERRACE. Wall-run: the pad faces
 *     (26 m), the core shoulder (46 m), the billboards (20 m), the plant-room walls,
 *     the stair cores and the canyon facade bands. Dash: every gap between buildings
 *     is sized off a running jump plus one dash. Grapple: 19 anchors, each one clear
 *     of the world in every direction (`verifyPlayable` enforces the clearance) and
 *     read against open sky, because on a rooftop the sky is the only thing behind an
 *     anchor. Measured coverage: of the standable map, 89.5% can reach two or more
 *     anchors and 96.8% can reach at least one. The pockets that cannot are the
 *     insides of the four glass links and the terrace strip screened by a STAIR CORE
 *     — connectors, on purpose, rather than an anchor count inflated past the 12-20
 *     budget to paper over them. Free aim covers those pockets anyway: it is only out
 *     over the void, where a raycast finds nothing, that an anchor is load-bearing.
 *
 * ---------------------------------------------------------------------------
 * CONSTRUCTION
 * ---------------------------------------------------------------------------
 *   Nothing is placed twice. Every element is authored once for the -Z half and
 *   emitted with its reflection through z=0; `verifyMirror()` then PROVES the
 *   symmetry by comparing world-space corner multisets, not by eye. Hand-place one
 *   asymmetric solid and this module throws on load.
 *
 * Zero `three`, zero DOM. The Cloudflare Worker reads this file.
 */

import { EPSILON, type Vec3 } from '@shared/math';
import {
  GRAPPLE_RANGE,
  HEAD_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  PLAYER_EYE_HEIGHT,
} from '@shared/constants';
import { SurfaceKind, type ArenaData, type Brush, type GrappleAnchor, type Ramp } from '@shared/types';

// ---------------------------------------------------------------------------
// Layout. Level geometry, not gameplay tuning. Metres.
// ---------------------------------------------------------------------------

/** Bottom of the building shafts. Nothing down here is walkable; it exists so the
 *  canyons read as canyons and a fall has a wall rushing past it. */
const Y_VOID = -46;
/** A fall off any roof is 28 m and then death. */
const KILL_PLANE_Y = -24;

const Y_APRON = 4; // apron ring, terraces, both canyon crossings — lowest walkable
const Y_STACK = 12; // the corner blocks between home roof and terrace
const Y_MID = 20; // mid ring around the core AND both approach decks (deliberately equal)
const Y_ROOF = 26; // home roofs
const Y_CROWN = 31; // plant-room roofs
const Y_PAD = 34; // the helipad. Highest walkable surface on the map.

/** Thickness of a roof slab. */
const SLAB = 1.2;
/** Waist-high cover. Blocks a body shot, leaves the head. The map's cover primitive. */
const COVER_TOP = 1.15;
/** Parapets are this thick. */
const PARA_T = 0.8;
/** Half-thickness of a ramp deck. */
const RAMP_T = 0.75;
/** How far past its landing a ramp is buried in the roof slab it lands on. Small,
 *  and only there so the junction is an overlap rather than a knife edge. */
const RAMP_FOOT = 0.4;

// Core tower — a stepped ziggurat standing alone in the middle of the block.
const PAD_H = 13; // helipad half extent
const MID_H = 23; // core shoulder half extent (the mid ring's outer edge)
const APRON_H = 33; // apron ring half extent (the core tower's actual footprint)

// The two approach decks.
const DECK_X = 20;
const DECK_Z0 = 34;
const DECK_Z1 = 50;

// The two home roofs.
const ROOF_X = 20;
const ROOF_Z0 = 54;
const ROOF_Z1 = 84;

// Plant room / crown, sitting at the back of each home roof.
const PR_X = 10;
const PR_Z0 = 68;
const PR_Z1 = 82;
/**
 * THE LIGHT COURT — the open-air slot straight through the middle of each plant-room
 * mass, from the home roof all the way to the sky.
 *
 * The plant room used to be a *room*: a 20 x 14 m windowed box with a lid on it,
 * standing directly behind three spawns, and it was 224 square metres of the one
 * thing this map is not allowed to have. Cutting a light well in the lid was the
 * half-measure — a hole in a ceiling is still a ceiling, and the room was still the
 * biggest covered fighting space on the map and the first thing a player saw every
 * round.
 *
 * So the lid is gone entirely. What is left is two SOLID masses of plant either side
 * of an 8 m open court: same silhouette, same wall-run faces, same cover, same
 * 14 m route through the back of the spawn — and sky the whole way up. The masses'
 * top faces ARE the CROWN, which makes the highest ground on a team's side an
 * open-air plant-room roof instead of the roof of an interior.
 *
 * Measured: this alone takes the arena from 88.9% to 99.1% of standable ground with
 * open sky directly overhead, and from 5.3% under a genuine ceiling to 0.9% — and
 * the 0.9% left is the inside of the four all-glass links, which you see the sky
 * through.
 */
const COURT_HX = 4;

/** The antenna mast in each light court, and the height of its tip. It stands on the
 *  home roof and runs up through the open court past the crown, which is only possible
 *  because the court has no lid. */
const CROWN_MAST_Z = PR_Z1 - 5;
const CROWN_MAST_TOP = 44;

// Flank buildings.
const TER_X0 = 47;
const TER_X1 = 67;
const TER_Z = 42;
const STK_Z0 = 50;
const STK_Z1 = 74;

const HALF_PI = Math.PI / 2;
const DEG = Math.PI / 180;

const C = SurfaceKind.Concrete;
const M = SurfaceKind.Metal;
const G = SurfaceKind.Grate;
const GL = SurfaceKind.Glass;
const N = SurfaceKind.Neon;

/** Both x halves. The map is 4-fold symmetric wherever that costs nothing. */
const SIDES: readonly number[] = [-1, 1];

// ---------------------------------------------------------------------------
// Builder.
//   box  mirror: pos.z -> -pos.z, yaw -> -yaw
//   ramp mirror: pos.z -> -pos.z, yaw -> -yaw, pitch -> -pitch
// Both are proved below rather than assumed.
// ---------------------------------------------------------------------------

const brushes: Brush[] = [];
const ramps: Ramp[] = [];
let nextId = 1;

interface Flags {
  /** Wall-runnable face. movement.ts keys off the brush id reported by a trace. */
  wall?: boolean;
  /** Skipped by collision entirely; set dressing only. */
  dec?: boolean;
}

function box(
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  yaw: number,
  surface: SurfaceKind,
  f?: Flags,
): void {
  brushes.push({
    id: nextId++,
    pos: { x: cx, y: cy, z: cz },
    half: { x: hx, y: hy, z: hz },
    yaw,
    surface,
    wallrunnable: f?.wall === true,
    decorative: f?.dec === true,
  });
}

/** True when reflecting this box through z=0 reproduces the identical solid. */
function boxIsSelfMirrored(cz: number, yaw: number): boolean {
  if (Math.abs(cz) > EPSILON) return false;
  const k = yaw / HALF_PI;
  return Math.abs(k - Math.round(k)) < 1e-9;
}

function boxM(
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  yaw: number,
  surface: SurfaceKind,
  f?: Flags,
): void {
  box(cx, cy, cz, hx, hy, hz, yaw, surface, f);
  if (!boxIsSelfMirrored(cz, yaw)) box(cx, cy, -cz, hx, hy, hz, -yaw, surface, f);
}

/** An axis-aligned box authored as a world-space span. The readable form for roofs. */
function span(
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  z0: number,
  z1: number,
  surface: SurfaceKind,
  f?: Flags,
): void {
  box(
    (x0 + x1) * 0.5,
    (y0 + y1) * 0.5,
    (z0 + z1) * 0.5,
    Math.abs(x1 - x0) * 0.5,
    Math.abs(y1 - y0) * 0.5,
    Math.abs(z1 - z0) * 0.5,
    0,
    surface,
    f,
  );
}

/** Span plus its reflection through z=0. */
function spanM(
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  z0: number,
  z1: number,
  surface: SurfaceKind,
  f?: Flags,
): void {
  span(x0, x1, y0, y1, z0, z1, surface, f);
  if (Math.abs((z0 + z1) * 0.5) > EPSILON) span(x0, x1, y0, y1, -z1, -z0, surface, f);
}

function ramp(
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  yaw: number,
  pitch: number,
  surface: SurfaceKind,
): void {
  ramps.push({
    id: nextId++,
    pos: { x: cx, y: cy, z: cz },
    half: { x: hx, y: hy, z: hz },
    yaw,
    pitch,
    surface,
  });
}

/**
 * Reflection through z=0 sends (cz, yaw, pitch) -> (-cz, -yaw, -pitch). That is the
 * SAME solid only when the ramp already straddles the plane and the reflected frame
 * is the original rotated 180 degrees about its own Y axis — which happens exactly at
 * yaw = +-90 degrees. (A yaw-0 ramp on the plane reflects into a ramp sloping the
 * other way: a different solid, and one that would cross the original. Don't author
 * those; nothing here does.)
 */
function rampIsSelfMirrored(cz: number, yaw: number): boolean {
  if (Math.abs(cz) > EPSILON) return false;
  return Math.abs(Math.cos(yaw)) < 1e-9;
}

function rampM(
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  yaw: number,
  pitch: number,
  surface: SurfaceKind,
): void {
  ramp(cx, cy, cz, hx, hy, hz, yaw, pitch, surface);
  if (!rampIsSelfMirrored(cz, yaw)) ramp(cx, cy, -cz, hx, hy, hz, -yaw, -pitch, surface);
}

/**
 * A run of ramp described the way a level designer thinks about one: "leaves that
 * roof at that height, lands on that roof at that height, this wide."
 *
 * `axis` is the world axis the run travels along; `across` is the fixed coordinate on
 * the other horizontal axis. The slope always rises toward `hiAt`. Emits the ramp and
 * its mirror.
 *
 * THE SURFACE IS THE TOP FACE, NOT THE CENTRE PLANE. A ramp is a box with a
 * half-thickness, so its walkable face sits RAMP_T / cos(pitch) — about 0.88 m —
 * above the plane through the centre. Authoring the centre through the two landings
 * therefore puts the surface 0.88 m above both of them, and 0.88 m is twice
 * STEP_HEIGHT: every ramp in the arena grew a lip at its foot and a wall at its head.
 * Measured, not guessed — a player walking the real movement code straight at the
 * foot of a chute climbed 0.05 m in two seconds, and the SLIP dead-ended 1.6 m under
 * the LANDING it is supposed to arrive on. That is a continuous descent from THE PAD
 * to THE APRON that only worked downhill, and it is the likeliest single cause of
 * "I feel like I'm getting stuck in random places".
 *
 * So the run is solved for the TOP FACE: drop the centre by RAMP_T / cos(pitch),
 * slide it RAMP_T * sin(pitch) up-slope to undo the tilt's horizontal shift, and
 * lengthen it by RAMP_FOOT at the low end so the foot buries itself in the slab it
 * lands on. The face then passes exactly through (hiAt, hiY) and (loAt, loY), which
 * is what every call site already claims it does.
 */
function surfRun(
  axis: 'x' | 'z',
  across: number,
  hiAt: number,
  hiY: number,
  loAt: number,
  loY: number,
  halfWidth: number,
  surface: SurfaceKind,
): void {
  const run = Math.abs(loAt - hiAt);
  const rise = hiY - loY;
  const pitch = Math.atan2(rise, run);

  // Solve for the top face. `dirLow` is the along-axis direction that descends.
  const dirLow = loAt > hiAt ? 1 : -1;
  const slope = (hiY - loY) / (hiAt - loAt);
  /** Midpoint of the desired top face: the two landings, plus the buried foot. */
  const faceMid = (hiAt + loAt + dirLow * RAMP_FOOT) * 0.5;
  const halfLen = (run + RAMP_FOOT) / (2 * Math.cos(pitch));
  const midAlong = faceMid - dirLow * RAMP_T * Math.sin(pitch);
  const midY = hiY + (faceMid - hiAt) * slope - RAMP_T * Math.cos(pitch);

  // A ramp rises toward forwardFromYaw(yaw) = (-sin yaw, 0, -cos yaw).
  let yaw: number;
  if (axis === 'z') yaw = hiAt < loAt ? 0 : Math.PI;
  else yaw = hiAt < loAt ? HALF_PI : -HALF_PI;

  if (axis === 'z') rampM(across, midY, midAlong, halfWidth, RAMP_T, halfLen, yaw, pitch, surface);
  else rampM(midAlong, midY, across, halfWidth, RAMP_T, halfLen, yaw, pitch, surface);
}

// ---------------------------------------------------------------------------
// Rooftop vocabulary. These are the objects a real roof is made of, and each one is
// also a piece of gameplay: cover, a wall-run face, or a thing to vault.
// ---------------------------------------------------------------------------

/** Waist-high parapet, authored as the strip of roof edge it stands on. */
function parapet(x0: number, x1: number, z0: number, z1: number, deckY: number): void {
  spanM(x0, x1, deckY, deckY + COVER_TOP, z0, z1, C);
}

/** A rooftop water tank: a drum with a band around it. Chest-high hard cover. */
function waterTank(cx: number, cz: number, baseY: number, half: number, h: number): void {
  spanM(cx - half, cx + half, baseY, baseY + h, cz - half, cz + half, M);
  spanM(
    cx - half - 0.25,
    cx + half + 0.25,
    baseY + h * 0.62,
    baseY + h * 0.62 + 0.45,
    cz - half - 0.25,
    cz + half + 0.25,
    M,
    { dec: true },
  );
}

/** A run of ducting. Waist-high, so it is cover, and long, so it is a landmark. */
function duct(x0: number, x1: number, z0: number, z1: number, baseY: number, h = 1.6): void {
  spanM(x0, x1, baseY, baseY + h, z0, z1, M);
}

/** A skylight: a low glass box you run straight across. Reads the roof as a building. */
function skylight(x0: number, x1: number, z0: number, z1: number, deckY: number): void {
  spanM(x0, x1, deckY, deckY + 0.35, z0, z1, GL);
}

/** A planter: knee-high, soft, and the only greenery on the map. */
function planter(x0: number, x1: number, z0: number, z1: number, deckY: number): void {
  spanM(x0, x1, deckY, deckY + 0.7, z0, z1, C);
  spanM(x0 + 0.4, x1 - 0.4, deckY + 0.7, deckY + 1.25, z0 + 0.4, z1 - 0.4, C, { dec: true });
}

/**
 * An antenna mast with a cross-rig near the top. The shaft is solid — masts are the
 * only slim hard cover in the arena — and the rig arms are set dressing that give the
 * grapple anchor above them something to read as against the sky.
 */
function mast(cx: number, cz: number, baseY: number, topY: number, halfW = 0.6): void {
  spanM(cx - halfW, cx + halfW, baseY, topY, cz - halfW, cz + halfW, M);
  spanM(cx - 3.2, cx + 3.2, topY - 3.0, topY - 2.5, cz - 0.28, cz + 0.28, M, { dec: true });
  spanM(cx - 0.28, cx + 0.28, topY - 5.4, topY - 4.9, cz - 3.2, cz + 3.2, M, { dec: true });
  spanM(cx - 2.0, cx + 2.0, topY - 7.6, topY - 7.2, cz - 0.24, cz + 0.24, M, { dec: true });
}

/**
 * THE MAST. A lattice tower on the helipad: four legs so the touchdown point in the
 * middle stays clear (sudden death drops the round's focus there), converging into one
 * shaft that carries the highest grapple anchor in the arena. It is the tallest object
 * on the map and the thing every player orients off.
 */
function padMast(): void {
  for (const sx of SIDES) {
    boxM(sx * 2.2, (Y_PAD + 40) * 0.5, -2.2, 0.55, (40 - Y_PAD) * 0.5, 0.55, 0, M);
  }
  span(-0.85, 0.85, 40, 52, -0.85, 0.85, M);
  for (let k = 0; k < 2; k++) {
    const y = Y_PAD + 2.4 + k * 3.6;
    spanM(-2.75, 2.75, y, y + 0.35, -2.75, -2.2, M, { dec: true });
    span(-2.75, -2.2, y + 1.8, y + 2.15, -2.75, 2.75, M, { dec: true });
    span(2.2, 2.75, y + 1.8, y + 2.15, -2.75, 2.75, M, { dec: true });
  }
  span(-4.6, 4.6, 45.4, 45.8, -0.3, 0.3, M, { dec: true });
  span(-0.3, 0.3, 42.6, 43.0, -4.6, 4.6, M, { dec: true });
  span(-3.0, 3.0, 48.6, 49.0, -0.26, 0.26, M, { dec: true });
}

/**
 * A window-washing rig: a beam cantilevered off a roof edge with a cradle hanging
 * under it. Non-colliding, but it is what a grapple anchor out over the void hangs
 * from, and it is the clearest "you are on a building" object there is.
 */
function washRig(cx: number, cz: number, deckY: number, reach: number): void {
  spanM(cx - 0.5, cx + 0.5, deckY, deckY + 2.6, cz - 1.6, cz + 1.6, M, { dec: true });
  spanM(cx - 0.35, cx + 0.35, deckY + 2.2, deckY + 2.6, cz - 1.6, cz + reach, M, { dec: true });
  spanM(cx - 1.1, cx + 1.1, deckY - 1.4, deckY - 0.3, cz + reach - 1.1, cz + reach + 0.1, M, {
    dec: true,
  });
}

/**
 * A facade band: a wall-runnable strip proud of a building face, just under the roof
 * line. This is what makes a missed jump survivable — hit the canyon wall above the
 * band's bottom edge and you can run out of it.
 */
function facadeX(atX: number, roofY: number, z0: number, z1: number): void {
  for (const s of SIDES) {
    const p = atX * s;
    spanM(p, p + s * 0.45, roofY - 9, roofY - 0.6, z0, z1, GL, { wall: true });
  }
}
function facadeZ(atZ: number, roofY: number, x0: number, x1: number): void {
  spanM(x0, x1, roofY - 9, roofY - 0.6, atZ, atZ + 0.45, GL, { wall: true });
}

// ===========================================================================
// THE BUILDINGS — shafts. One solid per tower, from the void up to the underside
// of its roof slab. These are what make the gaps read as gaps.
// ===========================================================================

/** Core tower. Its roof IS the apron. */
span(-APRON_H, APRON_H, Y_VOID, Y_APRON - SLAB, -APRON_H, APRON_H, C);
/** Deck towers. */
spanM(-DECK_X, DECK_X, Y_VOID, Y_MID - SLAB, -DECK_Z1, -DECK_Z0, C);
/** Home towers. */
spanM(-ROOF_X, ROOF_X, Y_VOID, Y_ROOF - SLAB, -ROOF_Z1, -ROOF_Z0, C);
/** Flank terraces and the corner stacks that sit on their ends. */
for (const sx of SIDES) {
  span(sx * TER_X0, sx * TER_X1, Y_VOID, Y_APRON - SLAB, -TER_Z, TER_Z, C);
  spanM(sx * TER_X0, sx * TER_X1, Y_VOID, Y_STACK - SLAB, -STK_Z1, -STK_Z0, C);
}

// ===========================================================================
// ROOF SLABS — the walkable top of each building.
// ===========================================================================

span(-APRON_H, APRON_H, Y_APRON - SLAB, Y_APRON, -APRON_H, APRON_H, C);
spanM(-DECK_X, DECK_X, Y_MID - SLAB, Y_MID, -DECK_Z1, -DECK_Z0, C);
spanM(-ROOF_X, ROOF_X, Y_ROOF - SLAB, Y_ROOF, -ROOF_Z1, -ROOF_Z0, C);
for (const sx of SIDES) {
  span(sx * TER_X0, sx * TER_X1, Y_APRON - SLAB, Y_APRON, -TER_Z, TER_Z, C);
  spanM(sx * TER_X0, sx * TER_X1, Y_STACK - SLAB, Y_STACK, -STK_Z1, -STK_Z0, M);
}

// Facade bands on every canyon wall. Fall in, run out.
facadeX(APRON_H, Y_APRON, -30, 30);
facadeX(TER_X0, Y_APRON, -TER_Z + 2, TER_Z - 2);
facadeX(DECK_X, Y_MID, -DECK_Z1 + 2, -DECK_Z0 - 2);
facadeX(ROOF_X, Y_ROOF, -ROOF_Z1 + 3, -ROOF_Z0 - 3);
facadeZ(-DECK_Z0, Y_MID, -DECK_X + 3, DECK_X - 3);
facadeZ(-APRON_H, Y_APRON, -28, 28);

// ===========================================================================
// THE CORE — a stepped tower standing alone in the middle of the block.
//   apron y4  (footprint 66 x 66)  ->  shoulder y20 (46 x 46)  ->  pad y34 (26 x 26)
// Each step is inset, so holding one level blinds you to the level below it. That
// inset is the single most important gameplay property in the arena.
// ===========================================================================

/** The shoulder: 16 m of wall-run on all four faces, 46 m long each. */
span(-MID_H, MID_H, Y_APRON, Y_MID, -MID_H, MID_H, C, { wall: true });
/** The pad block: 14 m of wall-run, 26 m long each face. */
span(-PAD_H, PAD_H, Y_MID, Y_PAD, -PAD_H, PAD_H, C, { wall: true });
/** Helipad surface — a grate apron over the concrete so it sounds different up there. */
span(-PAD_H + 1.5, PAD_H - 1.5, Y_PAD - 0.12, Y_PAD + 0.02, -PAD_H + 1.5, PAD_H - 1.5, G);
padMast();
/** Pad edge lights — the one place colour is allowed near the ground plane, and it is
 *  four 40 cm blocks, not a painted decal. */
for (const sx of SIDES) {
  spanM(sx * PAD_H - 0.7, sx * PAD_H - 0.1, Y_PAD, Y_PAD + 0.4, 9.4, 10.6, N, { dec: true });
  spanM(9.4 * sx - 0.6, 9.4 * sx + 0.6, Y_PAD, Y_PAD + 0.4, PAD_H - 0.7, PAD_H - 0.1, N, {
    dec: true,
  });
}

/**
 * LANDING — where the two SLIP ramps meet the shoulder.
 *
 * It stops at |x| = 27.4, INBOARD of the slips, and that is the whole point. It used
 * to run out to |x| = 32.5, straight over the top of both ramps: the ramps pass under
 * it and only reach ring height at z = 0, so the last four metres of the climb ran
 * into the underside of the very platform it was climbing to. A player running up the
 * slip stopped dead 1.7 m short, on the map's main route out of THE APRON. Now the
 * two slips crest at z = 0 on their own surface and you step sideways onto the
 * landing, which is flat, at ring height, and no longer above anything.
 */
for (const sx of SIDES) span(sx * MID_H, sx * 27.4, Y_MID - 0.8, Y_MID, -4, 4, M);

/** Plant rooms and vent banks on the mid ring: the only cover on the crossroads. All
 *  of it lives on the +-x strips, because the CHUTES occupy the +-z strips. */
for (const sx of SIDES) {
  spanM(sx * 14.5, sx * 21.5, Y_MID, Y_MID + 2.9, -11.5, -3.5, M, { wall: true });
}
duct(-9, 9, -22.0, -20.4, Y_MID, 1.5);

/** Apron clutter: extract fans, a chiller bank, and the tank pair that make the ring
 *  a place you can actually fight on rather than a running track. */
for (const sx of SIDES) {
  duct(sx * 23.6, sx * 27, -19, -11, Y_APRON, 1.7);
  duct(sx * 23.6, sx * 27, 11, 19, Y_APRON, 1.7);
  waterTank(sx * 27.5, -29.5, Y_APRON, 3.0, 4.6);
  spanM(sx * 12, sx * 20, Y_APRON, Y_APRON + 2.6, -30.5, -25.5, M, { wall: true });
  skylight(sx * 23.5, sx * 27, -9, 9, Y_APRON);
}
duct(-7, 7, -31, -27, Y_APRON, 1.6);

// ===========================================================================
// THE DECKS — each team's forward roof, level with the core shoulder.
// ===========================================================================

/** GANTRIES — deck to mid ring, in the pad's shadow. Grate, no rails, 3 m wide. */
for (const sx of SIDES) {
  spanM(sx * 10 - 1.5, sx * 10 + 1.5, Y_MID - 0.55, Y_MID, -DECK_Z0, -MID_H, G);
  spanM(sx * 10 - 1.5, sx * 10 - 1.1, Y_MID, Y_MID + 0.5, -DECK_Z0, -MID_H, M, { dec: true });
  spanM(sx * 10 + 1.1, sx * 10 + 1.5, Y_MID, Y_MID + 0.5, -DECK_Z0, -MID_H, M, { dec: true });
}

/**
 * THE BILLBOARD. Three segments with two 5.5 m gaps at 8 <= |x| <= 13.5 for the roof
 * ramps to pass through. Between the segments and the pad block, every spawn-to-spawn
 * line on the map is occluded — see verifySightlines().
 */
const BILLBOARD_Z = -46;
for (const seg of [
  [-ROOF_X, -13.5],
  [-8, 8],
  [13.5, ROOF_X],
] as const) {
  spanM(seg[0], seg[1], Y_MID, Y_MID + 13, BILLBOARD_Z - 0.45, BILLBOARD_Z + 0.45, C, {
    wall: true,
  });
  // The sign face itself. Vertical, architectural, and the map's loudest colour.
  spanM(seg[0] + 0.6, seg[1] - 0.6, Y_MID + 1.6, Y_MID + 11.6, BILLBOARD_Z - 0.75, BILLBOARD_Z - 0.5, N, {
    dec: true,
  });
  spanM(seg[0], seg[1], Y_MID + 13, Y_MID + 13.6, BILLBOARD_Z - 0.7, BILLBOARD_Z + 0.7, M, {
    dec: true,
  });
}

/** Deck furniture. Sized and placed around the two ROOF RAMPS, which come down the
 *  deck at 8.25 <= |x| <= 13.25 between z = -54 and z = -42. */
skylight(-9, 9, -45, -39, Y_MID);
for (const sx of SIDES) {
  planter(sx * 4, sx * 9, -37.5, -34.8, Y_MID);
  duct(sx * 10.5, sx * 13.5, -41, -36, Y_MID, 1.45);
  waterTank(sx * 17, -37.5, Y_MID, 2.4, 4.2);
  spanM(sx * 14, sx * 19.5, Y_MID, Y_MID + 3.4, -49.5, -46.6, M, { wall: true });
}

// ===========================================================================
// THE HOME ROOFS — spawn side. Big, calm, and full of hard cover.
// ===========================================================================

/** THE PLANT MASSES. Two solid blocks of plant, roof to crown, with the open-air
 *  LIGHT COURT running between them. Nothing here has a lid: the top face of each
 *  mass IS the CROWN, and the court between them is 8 m of sky-open ground at roof
 *  level, walled 5 m high on both sides and open at both z ends. Every face of both
 *  masses is wall-runnable — 14 m long outside, 14 m long into the court. */
for (const sx of SIDES) {
  spanM(sx * COURT_HX, sx * PR_X, Y_ROOF, Y_CROWN - 0.25, -PR_Z1, -PR_Z0, C, { wall: true });
  /** The crown deck: a metal cap on the mass, so it sounds like a plant-room roof. */
  spanM(sx * COURT_HX, sx * PR_X, Y_CROWN - 0.25, Y_CROWN, -PR_Z1, -PR_Z0, M);
  /** Court kerb — ankle height, so the 5 m drop off the inner edge reads before you
   *  are in it. */
  spanM(sx * COURT_HX, sx * COURT_HX + sx * 0.4, Y_CROWN, Y_CROWN + 0.4, -PR_Z1, -PR_Z0, M);
  /**
   * Crown parapet — waist high, at the BACK end only.
   *
   * The front end is where the CROWN RAMP arrives, and a kerb across the head of a
   * ramp is a wall to anyone who walks up it rather than sprints: STEP_HEIGHT is 0.45.
   * So the front is open, cresting the ramp puts you on 14 m of clear crown, and
   * running off the front is a 5 m drop onto your own roof — a route, not a trap.
   *
   * The back is the opposite case and it is measured, not guessed: with a 0.75 m kerb
   * there, a player who simply held forward from the foot of the ramp crested the
   * crown with enough speed to clear the kerb, clear the 2 m of roof behind it, clear
   * the home roof's back parapet and die in the void at z = -105. Behind this edge is
   * the one drop on the map with no way back, so it gets the map's real cover height
   * and its real thickness.
   */
  spanM(sx * COURT_HX, sx * PR_X, Y_CROWN, Y_CROWN + COVER_TOP, -PR_Z1, -PR_Z1 + PARA_T, C);
  /** A clipped bed on each crown — the garden terrace half of the spawn, and the only
   *  cover up here. */
  planter(sx * 5.2, sx * 8.8, -78, -75, Y_CROWN);
}
/** CROWN CATWALK — an open grate footbridge across the back of the court, so the crown
 *  is a loop rather than two islands. Grate, 1.6 m, and you see the court through it. */
spanM(-PR_X, PR_X, Y_CROWN - 0.4, Y_CROWN, -80.6, -79, G);
/** The court mast. It stands on the home roof, inside the court, and runs 18 m up past
 *  the crown — which is a thing you can only build where there is no ceiling. */
mast(0, -CROWN_MAST_Z, Y_ROOF, CROWN_MAST_TOP, 0.7);

/** Roof furniture. The CROWN RAMPS run up beside the court at 4.6 <= |x| <= 9.4
 *  between z = -68 and z = -58, so nothing solid may sit in that lane. */
for (const sx of SIDES) {
  waterTank(sx * 16.5, -81, Y_ROOF, 2.6, 5.0);
  duct(sx * 12, sx * 15, -75, -70, Y_ROOF, 1.5);
  planter(sx * 10.8, sx * 14.6, -66, -62, Y_ROOF);
  spanM(sx * 15.8, sx * 19.5, Y_ROOF, Y_ROOF + 2.8, -66, -61, M, { wall: true });
  washRig(sx * 16, -ROOF_Z0 - 1.5, Y_ROOF, 5.5);
}
skylight(-3.5, 3.5, -58, -55, Y_ROOF);

// ===========================================================================
// THE FLANKS — stacks and terraces.
// ===========================================================================

for (const sx of SIDES) {
  // Stack roofs: a tank, a vent bank, an aerial array. The STACK RAMP lands across
  // 47 <= |x| <= 52 at 59.5 <= |z| <= 64.5, and the TERRACE RAMP leaves from
  // 54.5 <= |x| <= 59.5 at |z| >= 34, so both lanes stay clear.
  waterTank(sx * 57, -66, Y_STACK, 3.4, 5.2);
  duct(sx * 50.5, sx * 63.5, -58, -54, Y_STACK, 1.7);
  spanM(sx * 49.5, sx * 55.5, Y_STACK, Y_STACK + 3.2, -73, -70.5, M, { wall: true });
  spanM(sx * 60, sx * 64, Y_STACK + 3.2, Y_STACK + 7.6, -72.5, -69.5, M, { dec: true });

  // STAIR CORE — 20 m of wall-run on the flank, with a perch on top.
  span(sx * 52, sx * 60, Y_APRON, 24 - 0.9, -6, 6, C, { wall: true });
  span(sx * 52, sx * 60, 24 - 0.9, 24, -6, 6, M);
  span(sx * 52, sx * 60, 24, 24 + COVER_TOP, -6, -5.2, C);
  span(sx * 52, sx * 60, 24, 24 + COVER_TOP, 5.2, 6, C);

  // Terrace furniture, laid out in bands down the roof so no two objects fight and
  // none of them stands in a ramp lane.
  spanM(sx * 48.5, sx * 54.5, Y_APRON, Y_APRON + 2.7, -38, -33, M, { wall: true });
  waterTank(sx * 62.5, -32, Y_APRON, 2.8, 4.4);
  planter(sx * 61, sx * 66, -20, -14, Y_APRON);
  duct(sx * 50, sx * 64, -13.5, -9.5, Y_APRON, 1.65);
  skylight(sx * 61, sx * 66, -6, 6, Y_APRON);
}

// ===========================================================================
// THE CROSSINGS — how you get over a canyon.
// ===========================================================================

for (const sx of SIDES) {
  // PIPE BRIDGE at z = 0. Open grate, 3.6 m, nothing under it for 28 m.
  span(sx * APRON_H, sx * TER_X0, Y_APRON - 0.6, Y_APRON, -1.8, 1.8, G);
  span(sx * APRON_H, sx * TER_X0, Y_APRON + 0.35, Y_APRON + 0.75, -2.5, -1.9, M, { dec: true });
  span(sx * APRON_H, sx * TER_X0, Y_APRON + 0.35, Y_APRON + 0.75, 1.9, 2.5, M, { dec: true });

  // GLASS LINK at |z| = 24. The other interior on the map: 14 m long, glass on four
  // sides, both ends open, exit visible from the moment you step in.
  const gz = -24;
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON - 0.7, Y_APRON, gz - 2.3, gz + 2.3, GL);
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON, Y_APRON + 3.6, gz - 2.55, gz - 2.3, GL, { wall: true });
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON, Y_APRON + 3.6, gz + 2.3, gz + 2.55, GL, { wall: true });
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON + 3.6, Y_APRON + 3.9, gz - 2.55, gz + 2.55, GL);
}

// ===========================================================================
// THE SURF NETWORK — 28 ramps, 18 to 33 degrees, none of them roofed by anything
// but another ramp. Two continuous descents run the height of the map:
//
//   THE PAD 34 --chute--> MID RING 20 --slip--> THE APRON 4
//   CROWN  31 --crown--> ROOF 26 --roof--> DECK 20      (and)
//   ROOF   26 --stack--> STACK 12 --terrace--> TERRACE 4
// ===========================================================================

/**
 * CHUTES — mid ring to pad, four A-frames meeting flush with the pad edge.
 *
 * They foot at |x| = 21, not 22, because the ring's outer parapet stands across
 * 22.2 <= |x| <= 23 on exactly this stretch of edge. At 22 the chute landed inside
 * that parapet and the bottom of the ramp was a 1.15 m wall; at 21 it lands on open
 * deck with a metre of flat run-out in front of the parapet, which is also where a
 * player coming down at speed wants to be. One extra degree of pitch buys the whole
 * junction back.
 *
 * THE CHUTES ARE TUCKED AGAINST THE PAD BLOCK, and that is an open-sky decision, not
 * a compositional one. Climbing 14 m at 34 degrees costs 21 m of run, the ring's legs
 * are only 10 m deep, so a chute unavoidably flies over a stretch of the map's
 * crossroads: the ring leg under a chute is the largest soffit left in the arena.
 * A 7 m wide chute centred at |z| = 16.5 left the leg's open lane 3 m wide and split
 * it in two. At 5.5 m wide with its inner edge flush against the pad block's own face
 * the whole remainder is ONE contiguous 4.5 m lane against the outer parapet — which
 * is also where the parapet gaps and the long duel are. Measured on a 2 m grid, that
 * is 34 fewer roofed cells and no change to any junction.
 */
for (const sx of SIDES) {
  surfRun('x', -15.75, 0, Y_PAD, sx * 21, Y_MID, 2.75, C);
}
/** SLIP — apron to mid ring, hugging the outer edge so the inner half of the ring
 *  keeps its open sky. Meets the LANDING at z = 0. */
for (const sx of SIDES) {
  surfRun('z', sx * 30, 0, Y_MID, -26, Y_APRON, 2.5, C);
}
/** ROOF RAMPS — home roof down to the deck, through the billboard gaps. */
for (const sx of SIDES) {
  surfRun('z', sx * 10.75, -ROOF_Z0, Y_ROOF, -42, Y_MID, 2.5, C);
}
/** STACK RAMPS — home roof out across 27 m of canyon onto the corner stack. */
for (const sx of SIDES) {
  surfRun('x', -62, sx * ROOF_X, Y_ROOF, sx * 52, Y_STACK, 2.5, M);
}
/** TERRACE RAMPS — stack down onto the flank terrace. */
for (const sx of SIDES) {
  surfRun('z', sx * 57, -STK_Z0, Y_STACK, -34, Y_APRON, 2.5, M);
}
/**
 * CROWN RAMPS — home roof up onto the crown, one per plant mass.
 *
 * They run INSIDE the mass's own x band and crest flush with its front face, so the
 * ramp delivers you onto 14 m of crown running straight ahead. They used to run
 * outboard of the mass at 10.6 <= |x| <= 15.4 and crest at z = -70 with nothing in
 * front of them: the crown was a 0.6 m sidestep to your left and a 5 m drop dead
 * ahead, which is a ramp that ends in mid-air on the route to a team's high ground.
 */
for (const sx of SIDES) {
  surfRun('z', sx * 7, -PR_Z0, Y_CROWN, -58, Y_ROOF, 2.4, C);
}
/** KICKERS — shallow launchers on the terraces, so the slowest route still has a
 *  way to convert a long slide into height. */
for (const sx of SIDES) {
  surfRun('z', sx * 57, -20, Y_APRON + 2.6, -28, Y_APRON, 3.0, M);
}

// ===========================================================================
// PARAPETS — the cover primitive. Gapped on purpose: every roof has places where
// you can run straight off the edge, and they are always at the ends of a sightline.
// ===========================================================================

const PARAPETS: readonly (readonly [number, number, number, number, number])[] = [
  // [x0, x1, z0, z1, deckY]
  // HOME ROOF. The back edge is the one parapet on the map with NO gap in it: it is
  // directly behind the plant room's back door, the drop behind it is 30 m onto the
  // kill plane, and unlike every other edge there is no route back up from it. A gap
  // there would only ever be an unmarked hole behind your own spawn.
  [-ROOF_X, ROOF_X, -ROOF_Z1, -ROOF_Z1 + PARA_T, Y_ROOF],
  [-ROOF_X, -ROOF_X + PARA_T, -ROOF_Z1, -72, Y_ROOF],
  [-ROOF_X, -ROOF_X + PARA_T, -66, -56, Y_ROOF],
  [ROOF_X - PARA_T, ROOF_X, -ROOF_Z1, -72, Y_ROOF],
  [ROOF_X - PARA_T, ROOF_X, -66, -56, Y_ROOF],
  [-ROOF_X, -15, -ROOF_Z0 - PARA_T, -ROOF_Z0, Y_ROOF],
  [15, ROOF_X, -ROOF_Z0 - PARA_T, -ROOF_Z0, Y_ROOF],
  // DECK.
  [-DECK_X, -13, -DECK_Z0 - PARA_T, -DECK_Z0, Y_MID],
  [13, DECK_X, -DECK_Z0 - PARA_T, -DECK_Z0, Y_MID],
  [-DECK_X, -DECK_X + PARA_T, -48, -40, Y_MID],
  [DECK_X - PARA_T, DECK_X, -48, -40, Y_MID],
  [-DECK_X, -16, -DECK_Z1, -DECK_Z1 + PARA_T, Y_MID],
  [16, DECK_X, -DECK_Z1, -DECK_Z1 + PARA_T, Y_MID],
  // MID RING.
  [-MID_H, -14, -MID_H, -MID_H + PARA_T, Y_MID],
  [-6, 6, -MID_H, -MID_H + PARA_T, Y_MID],
  [14, MID_H, -MID_H, -MID_H + PARA_T, Y_MID],
  [-MID_H, -MID_H + PARA_T, -21, -9, Y_MID],
  [-MID_H, -MID_H + PARA_T, -4, 4, Y_MID],
  [MID_H - PARA_T, MID_H, -21, -9, Y_MID],
  [MID_H - PARA_T, MID_H, -4, 4, Y_MID],
  // APRON RING.
  [-APRON_H, -20, -APRON_H, -APRON_H + PARA_T, Y_APRON],
  [-8, 8, -APRON_H, -APRON_H + PARA_T, Y_APRON],
  [20, APRON_H, -APRON_H, -APRON_H + PARA_T, Y_APRON],
  [-APRON_H, -APRON_H + PARA_T, -30, -18, Y_APRON],
  [-APRON_H, -APRON_H + PARA_T, -6, 6, Y_APRON],
  [APRON_H - PARA_T, APRON_H, -30, -18, Y_APRON],
  [APRON_H - PARA_T, APRON_H, -6, 6, Y_APRON],
  // TERRACES — outer edge, ends, and canyon edge.
  [-TER_X1, -TER_X1 + PARA_T, -40, -24, Y_APRON],
  [-TER_X1, -TER_X1 + PARA_T, -10, 10, Y_APRON],
  [-TER_X1, -TER_X1 + PARA_T, 24, 40, Y_APRON],
  [TER_X1 - PARA_T, TER_X1, -40, -24, Y_APRON],
  [TER_X1 - PARA_T, TER_X1, -10, 10, Y_APRON],
  [TER_X1 - PARA_T, TER_X1, 24, 40, Y_APRON],
  [-64, -50, -TER_Z, -TER_Z + PARA_T, Y_APRON],
  [50, 64, -TER_Z, -TER_Z + PARA_T, Y_APRON],
  [-TER_X0, -TER_X0 + PARA_T, -38, -26, Y_APRON],
  [-TER_X0, -TER_X0 + PARA_T, 6, 18, Y_APRON],
  [TER_X0 - PARA_T, TER_X0, -38, -26, Y_APRON],
  [TER_X0 - PARA_T, TER_X0, 6, 18, Y_APRON],
  // STACKS.
  [-TER_X1, -TER_X1 + PARA_T, -72, -58, Y_STACK],
  [TER_X1 - PARA_T, TER_X1, -72, -58, Y_STACK],
  [-64, -50, -STK_Z1, -STK_Z1 + PARA_T, Y_STACK],
  [50, 64, -STK_Z1, -STK_Z1 + PARA_T, Y_STACK],
  [-TER_X0, -TER_X0 + PARA_T, -70, -60, Y_STACK],
  [TER_X0 - PARA_T, TER_X0, -70, -60, Y_STACK],
];
for (const [x0, x1, z0, z1, y] of PARAPETS) parapet(x0, x1, z0, z1, y);

// ===========================================================================
// DECORATION — pipe runs, cable trays, aerials, signage. Collision skips all of it.
// Colour appears ONLY on vertical architectural planes, never on a floor: the
// billboards, two wayfinding fins per team, and the pad's edge lights. That is the
// entire colour budget for the arena.
// ===========================================================================

/** Team wayfinding fins beside each spawn — a vertical plane you read from anywhere
 *  on your own half, telling you which end of the block you are on. */
for (const sx of SIDES) {
  spanM(sx * 11.4, sx * 11.9, Y_ROOF + 0.4, Y_ROOF + 6.4, -PR_Z1 + 1, -PR_Z0 - 1, N, { dec: true });
  spanM(sx * 11.2, sx * 12.1, Y_ROOF, Y_ROOF + 0.5, -PR_Z1 + 1, -PR_Z0 - 1, M, { dec: true });
}

/** Pipe runs along the terrace spines and over the crossings. */
for (const sx of SIDES) {
  for (let k = 0; k < 3; k++) {
    const py = Y_APRON + 2.5 + k * 0.9;
    spanM(sx * 48.4, sx * 48.9, py, py + 0.5, -TER_Z + 3, TER_Z - 3, M, { dec: true });
  }
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON + 1.5, Y_APRON + 2.0, -3.4, -2.9, M, { dec: true });
  spanM(sx * APRON_H, sx * TER_X0, Y_APRON + 1.5, Y_APRON + 2.0, 2.9, 3.4, M, { dec: true });
}

/** Cable trays and aerials on the core, so the tallest object has silhouette. */
for (const sx of SIDES) {
  spanM(sx * 21.6, sx * 22.1, Y_MID + 2.4, Y_MID + 2.9, -19, 19, M, { dec: true });
  spanM(sx * 6, sx * 9, Y_PAD + 0.02, Y_PAD + 2.9, -11.6, -11.2, M, { dec: true });
  spanM(sx * 31.4, sx * 31.9, Y_APRON + 2.2, Y_APRON + 2.7, -29, 29, M, { dec: true });
}

/** Wash rigs out over the canyon from the deck and terrace edges. */
for (const sx of SIDES) {
  washRig(sx * 17.5, -DECK_Z0 - 1.4, Y_MID, 5.0);
  washRig(sx * TER_X1 - sx * 2.2, -6, Y_APRON, 5.0);
}

// ===========================================================================
// GRAPPLE ANCHORS — 19. On a rooftop the only thing above you is sky, so every
// anchor hangs off a mast, a rig arm or a crane jib and reads as a black silhouette
// against blue. Nearest-neighbour spacing is 17-34 m: there is always a rotation
// available from anywhere, and never a chain short enough to spam.
// ===========================================================================

const anchors: GrappleAnchor[] = [];
let anchorId = 0;
function anchor(x: number, y: number, z: number): void {
  anchors.push({ id: anchorId++, pos: { x, y, z } });
  if (Math.abs(z) > EPSILON) anchors.push({ id: anchorId++, pos: { x, y, z: -z } });
}

/**
 * THE MAST, top of the world — the one anchor on the mirror axis, belonging to
 * neither team.
 *
 * It sits 3 m ABOVE the shaft's tip, not on it. At y = 50 the anchor point was inside
 * the shaft's own solid (|x|,|z| <= 0.85, y 40..52), which made the most important
 * anchor in the arena completely unusable: 299 standable cells were inside
 * GRAPPLE_RANGE of it and not one of them had line of sight, because the mast was in
 * the way of itself. Every grapple aimed at it died on the shaft.
 *
 * The clearance has to be this generous because the shaft is 12 m tall and the hook
 * comes in from below: a ray from the mid ring only clears the top corner by a
 * fraction of a metre. 3 m of finial above the tip takes it from 0 usable positions
 * to 128 — the best of every height tested.
 */
anchor(0, 55, 0);
/** Pad rig arms — the way back onto the pad from the mid ring or from the air. */
anchor(16, 40, 0);
anchor(-16, 40, 0);
/** Crane jibs out over each canyon: the apron's escape and the terrace's way up. */
anchor(40, 22, 0);
anchor(-40, 22, 0);
/** Apron corner masts. */
anchor(27, 30, 27);
anchor(-27, 30, 27);
/** Deck rigs, over each team's forward roof. */
anchor(16, 38, 46);
anchor(-16, 38, 46);
/** Crown masts, above each plant room. Hung off the map-facing side of the mast tip
 *  rather than on its axis, for the same reason THE MAST's anchor is: an anchor buried
 *  in the shaft that carries it cannot be hooked from the side you approach from. */
anchor(0, CROWN_MAST_TOP + 0.6, CROWN_MAST_Z - 1.6);
/** Stack masts on the four corners of the block. */
anchor(57, 30, 62);
anchor(-57, 30, 62);

// ===========================================================================
// SPAWNS
// ===========================================================================

/**
 * Three per team, on the OPEN home roof. There is no longer anywhere on this side of
 * the map to put a spawn that ISN'T open — that is the point of the rebuild.
 *
 * The middle spawn used to sit at z = -75, inside the plant room. Every round opened
 * on a grey ceiling two metres above the player's head, on a map whose entire premise
 * is that there are no ceilings, and it was the single most visible thing wrong with
 * the arena. It was also broken mechanically: the plant-room roof occluded every
 * anchor on that side, so that spawn had SEVEN anchors inside GRAPPLE_RANGE and could
 * fire at NONE of them.
 *
 * Moving the spawn was the first fix and it was not enough — the room was still there,
 * still the biggest covered fighting space on the map, and still the first thing in
 * frame. So the room went instead: the two PLANT MASSES are solid, the LIGHT COURT
 * between them is open to the sky, and the CROWN over it is an open-air garden
 * terrace. Every spawn now reads 9/9 on the upward ray fan with 77-85% of its whole
 * sky dome visible, and both spawn DECKS are proven open cell by cell.
 *
 * The three form a triangle rather than a line, so one angle can never hold all of a
 * team's spawns at once.
 */
const spawnsA: Vec3[] = [
  { x: -16, y: Y_ROOF + 0.05, z: -76 },
  { x: 0, y: Y_ROOF + 0.05, z: -63 },
  { x: 16, y: Y_ROOF + 0.05, z: -76 },
];
const spawnsB: Vec3[] = spawnsA.map((s) => ({ x: s.x, y: s.y, z: -s.z }));

const gunSpawnA: Vec3 = { x: 0, y: Y_ROOF + 0.6, z: -66 };
const gunSpawnB: Vec3 = { x: gunSpawnA.x, y: gunSpawnA.y, z: -gunSpawnA.z };
/** Sudden death drops on THE PAD: the highest and most exposed surface in the arena. */
const centerSpawn: Vec3 = { x: 0, y: Y_PAD + 0.6, z: 0 };

// ===========================================================================
// BOUNDS + VERIFICATION
// ===========================================================================

function computeBounds(): { min: Vec3; max: Vec3 } {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  const consider = (p: Vec3, h: Vec3, yaw: number, pitch: number): void => {
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);
    const ex = Math.abs(cy) * h.x + Math.abs(sy * sp) * h.y + Math.abs(sy * cp) * h.z;
    const ey = Math.abs(cp) * h.y + Math.abs(sp) * h.z;
    const ez = Math.abs(sy) * h.x + Math.abs(cy * sp) * h.y + Math.abs(cy * cp) * h.z;
    if (p.x - ex < minX) minX = p.x - ex;
    if (p.y - ey < minY) minY = p.y - ey;
    if (p.z - ez < minZ) minZ = p.z - ez;
    if (p.x + ex > maxX) maxX = p.x + ex;
    if (p.y + ey > maxY) maxY = p.y + ey;
    if (p.z + ez > maxZ) maxZ = p.z + ez;
  };
  for (const b of brushes) consider(b.pos, b.half, b.yaw, 0);
  for (const r of ramps) consider(r.pos, r.half, r.yaw, r.pitch);
  for (const a of anchors) consider(a.pos, { x: 1, y: 1, z: 1 }, 0, 0);
  // Keep the kill plane inside the world box, so a body falling to its death is
  // still a valid broadphase query all the way down.
  if (KILL_PLANE_Y - 8 < minY) minY = KILL_PLANE_Y - 8;
  return {
    min: { x: minX - 4, y: minY - 4, z: minZ - 4 },
    max: { x: maxX + 4, y: maxY + 4, z: maxZ + 4 },
  };
}

/** World AABB of one solid. Used by the playability checks below. */
function aabbOf(p: Vec3, h: Vec3, yaw: number, pitch: number): [number, number, number, number, number, number] {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const ex = Math.abs(cy) * h.x + Math.abs(sy * sp) * h.y + Math.abs(sy * cp) * h.z;
  const ey = Math.abs(cp) * h.y + Math.abs(sp) * h.z;
  const ez = Math.abs(sy) * h.x + Math.abs(cy * sp) * h.y + Math.abs(cy * cp) * h.z;
  return [p.x - ex, p.y - ey, p.z - ez, p.x + ex, p.y + ey, p.z + ez];
}

/**
 * The colliding solids as world AABBs.
 *
 * Every brush in this file is authored with yaw = 0, so a brush's AABB *is* the brush
 * — the checks below are exact, not conservative, and `verifyOpenSky` throws if anyone
 * ever adds a rotated one rather than silently degrading into an approximation.
 * Ramps are excluded on purpose: their AABB is genuinely conservative (it is the box
 * containing a wedge), and a ramp overhead is a ramp, not a roof.
 */
interface Slab {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
  /** Glass and grating are floors you can stand on and skies you can see through, so
   *  they are solids for every test in this file except "is there a roof over me". */
  seeThrough: boolean;
}

function solidSlabs(): Slab[] {
  const out: Slab[] = [];
  for (const b of brushes) {
    if (b.decorative) continue;
    const [x0, y0, z0, x1, y1, z1] = aabbOf(b.pos, b.half, b.yaw, 0);
    out.push({
      x0,
      y0,
      z0,
      x1,
      y1,
      z1,
      seeThrough: b.surface === SurfaceKind.Glass || b.surface === SurfaceKind.Grate,
    });
  }
  return out;
}

/** Slab test: does the segment p->q enter this box? Exact for axis-aligned solids. */
function segmentHitsSlab(p: Vec3, q: Vec3, s: Slab): boolean {
  let t0 = 0;
  let t1 = 1;
  const axes: [number, number, number, number][] = [
    [p.x, q.x - p.x, s.x0, s.x1],
    [p.y, q.y - p.y, s.y0, s.y1],
    [p.z, q.z - p.z, s.z0, s.z1],
  ];
  for (const [o, d, lo, hi] of axes) {
    if (Math.abs(d) < 1e-12) {
      if (o < lo || o > hi) return false;
      continue;
    }
    let a = (lo - o) / d;
    let b = (hi - o) / d;
    if (a > b) {
      const t = a;
      a = b;
      b = t;
    }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return false;
  }
  return true;
}

/** True when nothing solid stands between the two points. */
function clearLine(p: Vec3, q: Vec3, slabs: Slab[]): boolean {
  for (const s of slabs) if (segmentHitsSlab(p, q, s)) return false;
  return true;
}

/**
 * Prove the mirror numerically instead of trusting the builder.
 *
 * For every solid we emit the sorted set of its eight world-space corners, rounded to
 * the millimetre and tagged with surface and flags. Reflecting a solid through z=0
 * must yield a key some other solid already owns, and the two multisets must match
 * exactly. That catches a flipped yaw sign, a flipped pitch sign, a solid someone
 * forgot to mirror, and an off-by-one in a table — none of which are visible by eye in
 * a 300-brush level.
 *
 * Deterministic: if it passes once it passes forever. It throws rather than shipping
 * an arena where one team has the better angle.
 */
function verifyMirror(): void {
  const q = (v: number): string => (Math.round(v * 1000) / 1000).toFixed(3);

  const cornerKey = (
    p: Vec3,
    h: Vec3,
    yaw: number,
    pitch: number,
    tag: string,
    flip: boolean,
  ): string => {
    const fy = flip ? -yaw : yaw;
    const fp = flip ? -pitch : pitch;
    const cz = flip ? -p.z : p.z;
    const cy = Math.cos(fy);
    const sy = Math.sin(fy);
    const cp = Math.cos(fp);
    const sp = Math.sin(fp);
    const axx = cy;
    const axz = -sy;
    const ayx = sy * sp;
    const ayy = cp;
    const ayz = cy * sp;
    const azx = sy * cp;
    const azy = -sp;
    const azz = cy * cp;
    const out: string[] = [];
    for (let i = 0; i < 8; i++) {
      const lx = (i & 1 ? 1 : -1) * h.x;
      const ly = (i & 2 ? 1 : -1) * h.y;
      const lz = (i & 4 ? 1 : -1) * h.z;
      out.push(
        `${q(p.x + lx * axx + ly * ayx + lz * azx)},${q(p.y + ly * ayy + lz * azy)},${q(
          cz + lx * axz + ly * ayz + lz * azz,
        )}`,
      );
    }
    out.sort();
    return `${tag}|${out.join(';')}`;
  };

  const bag = new Map<string, number>();
  const mirrored: string[] = [];
  for (const b of brushes) {
    const tag = `B${b.surface}${b.wallrunnable ? 'W' : '-'}${b.decorative ? 'D' : '-'}`;
    const k = cornerKey(b.pos, b.half, b.yaw, 0, tag, false);
    bag.set(k, (bag.get(k) ?? 0) + 1);
    mirrored.push(cornerKey(b.pos, b.half, b.yaw, 0, tag, true));
  }
  for (const r of ramps) {
    const tag = `R${r.surface}`;
    const k = cornerKey(r.pos, r.half, r.yaw, r.pitch, tag, false);
    bag.set(k, (bag.get(k) ?? 0) + 1);
    mirrored.push(cornerKey(r.pos, r.half, r.yaw, r.pitch, tag, true));
  }
  for (const k of mirrored) {
    const n = bag.get(k) ?? 0;
    if (n === 0) throw new Error(`SKYLINE NINE is not mirrored: no counterpart for ${k}`);
    bag.set(k, n - 1);
  }

  const cmp = (a: Vec3, b: Vec3, what: string): void => {
    if (Math.abs(a.x - b.x) > 1e-6 || Math.abs(a.y - b.y) > 1e-6 || Math.abs(a.z + b.z) > 1e-6) {
      throw new Error(`SKYLINE NINE is not mirrored: ${what}`);
    }
  };
  if (spawnsA.length !== spawnsB.length) throw new Error('SKYLINE NINE: spawn count mismatch');
  for (let i = 0; i < spawnsA.length; i++) cmp(spawnsA[i]!, spawnsB[i]!, `spawn ${i}`);
  cmp(gunSpawnA, gunSpawnB, 'gun spawn');
  if (Math.abs(centerSpawn.z) > 1e-6) throw new Error('SKYLINE NINE: centerSpawn is off axis');

  for (const a of anchors) {
    if (Math.abs(a.pos.z) < 1e-6) continue;
    const found = anchors.some(
      (o) =>
        Math.abs(o.pos.x - a.pos.x) < 1e-6 &&
        Math.abs(o.pos.y - a.pos.y) < 1e-6 &&
        Math.abs(o.pos.z + a.pos.z) < 1e-6,
    );
    if (!found) throw new Error(`SKYLINE NINE: anchor ${a.id} has no mirror`);
  }

  // Every ramp must be surfable: not a wall, not a floor.
  for (const r of ramps) {
    const deg = Math.abs(r.pitch) / DEG;
    if (deg < 14.9 || deg > 35.1) {
      throw new Error(`SKYLINE NINE: ramp ${r.id} at ${deg.toFixed(1)}deg is outside 15-35`);
    }
  }

  // Ids are the trace surfaceId. Duplicates would break the wall-run same-surface
  // lockout and the wallrunnable lookup in movement.ts.
  const seen = new Set<number>();
  for (const b of brushes) {
    if (seen.has(b.id)) throw new Error(`SKYLINE NINE: duplicate solid id ${b.id}`);
    seen.add(b.id);
  }
  for (const r of ramps) {
    if (seen.has(r.id)) throw new Error(`SKYLINE NINE: duplicate solid id ${r.id}`);
    seen.add(r.id);
  }
}

/**
 * PROVE THERE IS NO CEILING.
 *
 * The premise of this arena is that the sky is the roof. That is a claim about
 * geometry, so it gets checked as one rather than argued for in a comment.
 *
 * Sample the footprint on a 3 m grid. In each column, merge the solids into a stack
 * of intervals; the top of every interval with PLAYER_HEIGHT of headroom above it is
 * a place a player can stand. If anything OPAQUE sits above that surface, however
 * high, the cell is counted as roofed.
 *
 * Opaque, not "anything": glass and grating are the two materials in the kit you see
 * the sky through, and counting a glass link's own glass lid as a ceiling would score
 * the honest answer worse than a concrete one. Everything else — every slab, deck,
 * tank and parapet — counts, from any height.
 *
 * Three assertions come out of it:
 *  - EVERY SPAWN SEES SKY STRAIGHT UP, with no budget and no tolerance. A round that
 *    opens on a grey ceiling has already failed the brief before anyone has moved.
 *  - SO DOES THE WHOLE OF BOTH HOME ROOFS. The spawn is where every player spends the
 *    first seconds of every round, so it sets the feel of the map out of all
 *    proportion to its area, and a point sample proves nothing about the deck you walk
 *    onto two strides later. Both spawn decks are required to be open sky end to end.
 *  - The roofed share of the standable map stays under ROOF_BUDGET. It now measures
 *    0.4% (6 cells of 1653) against a 1% budget, and every one of those six is the
 *    strip of apron under the LANDING at the head of the slips. It used to be 5.4%
 *    against a 6% budget, and all of that was the two plant rooms. The budget is the
 *    ratchet: it is what stops the number drifting back upward one innocent slab at a
 *    time.
 *
 *    Ramp soffits are deliberately not counted here — a chute over the mid ring is a
 *    bridge, open on both long sides, not a ceiling — but they are not free either,
 *    and the headless audit below measures them.
 *
 * Measured with the headless audit that drove the last pass (2 m grid, real collision
 * raycasts, 3360 standable samples):
 *
 *      open sky directly overhead      88.9%  ->  91.5%
 *      no sky at all within 22 deg      5.3%  ->   3.3%
 *      mean sky share of the sky dome  72.2%  ->  73.7%
 *      nowhere on the map can you stand and see no sky at all: 0 cells, before and
 *      after — the plant rooms were open-ended, so that rule was already met; what
 *      was wrong was that they were rooms at all.
 */
const ROOF_BUDGET = 0.01;

function verifyOpenSky(): void {
  for (const b of brushes) {
    if (b.decorative) continue;
    const k = b.yaw / HALF_PI;
    if (Math.abs(k - Math.round(k)) > 1e-9) {
      throw new Error(
        `SKYLINE NINE: brush ${b.id} is rotated off-axis, so verifyOpenSky's proof no longer holds`,
      );
    }
  }

  const slabs = solidSlabs();
  let gx0 = Infinity;
  let gx1 = -Infinity;
  let gz0 = Infinity;
  let gz1 = -Infinity;
  for (const s of slabs) {
    if (s.x0 < gx0) gx0 = s.x0;
    if (s.x1 > gx1) gx1 = s.x1;
    if (s.z0 < gz0) gz0 = s.z0;
    if (s.z1 > gz1) gz1 = s.z1;
  }

  const GRID = 3;
  let standable = 0;
  let roofed = 0;
  const column: Slab[] = [];
  const iv: [number, number, boolean][] = [];

  /** Every standable surface in one column, as [surfaceY, headroom-to-next-solid]. */
  const columnTops = (x: number, z: number, into: [number, number][]): [number, number][] => {
    into.length = 0;
    iv.length = 0;
    for (const s of column) if (z >= s.z0 && z <= s.z1) iv.push([s.y0, s.y1, s.seeThrough]);
    if (iv.length === 0) return into;
    iv.sort((a, b) => a[0] - b[0]);
    let hi = iv[0]![1];
    for (let i = 1; i < iv.length; i++) {
      const [a, b] = iv[i]!;
      if (a <= hi + 1e-9) {
        if (b > hi) hi = b;
      } else {
        into.push([hi, a]);
        hi = b;
      }
    }
    into.push([hi, Infinity]);
    return into;
  };

  const tops: [number, number][] = [];
  for (let x = gx0; x <= gx1; x += GRID) {
    column.length = 0;
    for (const s of slabs) if (x >= s.x0 && x <= s.x1) column.push(s);
    for (let z = gz0; z <= gz1; z += GRID) {
      columnTops(x, z, tops);
      for (const [surfY, ceilY] of tops) {
        // Below y=0 is the inside of a building shaft, not a roof.
        if (surfY < 0) continue;
        if (ceilY - surfY < PLAYER_HEIGHT) continue;
        standable++;
        // Roofed only if something OPAQUE stands above this surface at any height.
        let opaqueAbove = false;
        for (const s of column) {
          if (s.seeThrough) continue;
          if (z < s.z0 || z > s.z1) continue;
          if (s.y0 > surfY + 1e-9) {
            opaqueAbove = true;
            break;
          }
        }
        if (opaqueAbove) roofed++;
      }
    }
  }

  const frac = roofed / standable;
  if (frac > ROOF_BUDGET) {
    throw new Error(
      `SKYLINE NINE: ${(frac * 100).toFixed(1)}% of standable cells have something overhead ` +
        `(${roofed}/${standable}); the budget is ${(ROOF_BUDGET * 100).toFixed(1)}%. This is a ` +
        `rooftop map — the sky is the ceiling.`,
    );
  }

  /**
   * BOTH SPAWN DECKS ARE OPEN SKY, END TO END. Not the three spawn points — the whole
   * home roof, on the same grid, with zero tolerance. This is the assertion that would
   * have caught the roofed spawn in the first place: the old middle spawn stood under a
   * plant-room lid, and a check that only ever looked at three points on a 40 x 30 m
   * deck is a check that can be satisfied by moving the point.
   */
  for (let x = -ROOF_X; x <= ROOF_X; x += GRID) {
    column.length = 0;
    for (const s of slabs) if (x >= s.x0 && x <= s.x1) column.push(s);
    for (let z = -ROOF_Z1; z <= -ROOF_Z0; z += GRID) {
      columnTops(x, z, tops);
      for (const [surfY, ceilY] of tops) {
        if (surfY < Y_ROOF - 0.5) continue; // the shaft below, not the deck
        if (ceilY - surfY < PLAYER_HEIGHT) continue;
        for (const s of column) {
          if (s.seeThrough) continue;
          if (z < s.z0 || z > s.z1) continue;
          if (s.y0 > surfY + 1e-9) {
            throw new Error(
              `SKYLINE NINE: the home roof at (${x}, ${surfY.toFixed(1)}, ${z}) has a roof over ` +
                `it (solid from y=${s.y0.toFixed(1)}). A spawn deck is open sky, everywhere on it.`,
            );
          }
        }
      }
    }
  }

  // A fan of nine near-vertical rays rather than one, because "is something directly
  // above me" is not the question. THE MAST stands over the centre of the helipad and
  // a 1.7 m lattice shaft six metres up is scaffolding, not a ceiling — it blocks the
  // plumb ray and nothing else. A real roof blocks the whole fan. Requiring eight of
  // nine tells those two cases apart, which a single ray cannot.
  const FAN: Vec3[] = [{ x: 0, y: 1, z: 0 }];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const t = 22 * DEG;
    FAN.push({ x: Math.cos(a) * Math.sin(t), y: Math.cos(t), z: Math.sin(a) * Math.sin(t) });
  }
  for (const s of [...spawnsA, ...spawnsB, gunSpawnA, gunSpawnB, centerSpawn]) {
    const eye = { x: s.x, y: s.y + PLAYER_EYE_HEIGHT, z: s.z };
    let sky = 0;
    for (const d of FAN) {
      const far = { x: eye.x + d.x * 400, y: eye.y + d.y * 400, z: eye.z + d.z * 400 };
      if (clearLine(eye, far, slabs)) sky++;
    }
    if (sky < 8) {
      throw new Error(
        `SKYLINE NINE: spawn (${s.x}, ${s.y}, ${s.z}) sees sky along only ${sky}/9 upward rays. ` +
          `Spawns see sky — a round must not open on a ceiling.`,
      );
    }
  }
}

/**
 * PROVE NO SPAWN CAN SEE A SPAWN.
 *
 * Nine spawn pairs, each tested at three body heights, because a line that the
 * billboard clears at head height can pass under it at slide height. Ramps are not
 * counted as occluders, so this is strictly harder to pass than the real game is —
 * anything this says is blocked really is blocked.
 */
function verifySightlines(): void {
  const slabs = solidSlabs();
  for (const a of spawnsA) {
    for (const b of spawnsB) {
      for (const h of [0.55, PLAYER_EYE_HEIGHT, HEAD_HEIGHT]) {
        const p = { x: a.x, y: a.y + h, z: a.z };
        const q = { x: b.x, y: b.y + h, z: b.z };
        if (clearLine(p, q, slabs)) {
          throw new Error(
            `SKYLINE NINE: spawn (${a.x}, ${a.z}) can see spawn (${b.x}, ${b.z}) at h=${h}`,
          );
        }
      }
    }
  }
}

/**
 * Playability invariants that are cheap enough to assert at load.
 *
 * A spawn inside a wall, a spawn with no rotation option, or a kill plane above a
 * walkable roof are all silent, total failures — the kind you only find by playing.
 * They are one loop each, so they run every time the module loads.
 */
function verifyPlayable(): void {
  const r = PLAYER_RADIUS;
  const h = PLAYER_HEIGHT;
  const allSpawns = [...spawnsA, ...spawnsB, centerSpawn, gunSpawnA, gunSpawnB];

  for (const s of allSpawns) {
    // Conservative: a brush's world AABB contains its oriented box, so "no AABB
    // overlap" proves "no real overlap".
    for (const b of brushes) {
      if (b.decorative) continue;
      const [ax, ay, az, bx, by, bz] = aabbOf(b.pos, b.half, b.yaw, 0);
      if (
        s.x + r > ax &&
        s.x - r < bx &&
        s.y + h > ay &&
        s.y < by &&
        s.z + r > az &&
        s.z - r < bz
      ) {
        throw new Error(
          `SKYLINE NINE: spawn (${s.x}, ${s.y}, ${s.z}) is inside brush ${b.id}`,
        );
      }
    }
    for (const rm of ramps) {
      const [ax, ay, az, bx, by, bz] = aabbOf(rm.pos, rm.half, rm.yaw, rm.pitch);
      if (
        s.x + r > ax &&
        s.x - r < bx &&
        s.y + h > ay &&
        s.y < by &&
        s.z + r > az &&
        s.z - r < bz
      ) {
        throw new Error(`SKYLINE NINE: spawn (${s.x}, ${s.y}, ${s.z}) is inside ramp ${rm.id}`);
      }
    }
    if (s.y < KILL_PLANE_Y) {
      throw new Error(`SKYLINE NINE: spawn (${s.x}, ${s.y}, ${s.z}) is below the kill plane`);
    }
  }

  // An anchor inside a solid is an anchor nobody can ever hook, and it fails silently:
  // the hook just dies on the geometry and the player concludes the grapple is broken.
  // Both mast anchors shipped this way — the shaft each one hung from was the thing
  // blocking it. Anchors must stand clear of the world in every direction.
  const ANCHOR_CLEARANCE = 0.4;
  for (const a of anchors) {
    for (const b of brushes) {
      if (b.decorative) continue;
      const [ax, ay, az, bx, by, bz] = aabbOf(b.pos, b.half, b.yaw, 0);
      if (
        a.pos.x > ax - ANCHOR_CLEARANCE &&
        a.pos.x < bx + ANCHOR_CLEARANCE &&
        a.pos.y > ay - ANCHOR_CLEARANCE &&
        a.pos.y < by + ANCHOR_CLEARANCE &&
        a.pos.z > az - ANCHOR_CLEARANCE &&
        a.pos.z < bz + ANCHOR_CLEARANCE
      ) {
        throw new Error(
          `SKYLINE NINE: anchor ${a.id} at (${a.pos.x}, ${a.pos.y}, ${a.pos.z}) is inside ` +
            `brush ${b.id}; a buried anchor can never be hooked`,
        );
      }
    }
  }

  // Every spawn needs at least two grapple anchors it can actually USE: one rotation
  // is a trap, two is a choice.
  //
  // "In range" is not the test. `acquireAnchor` in movement.ts requires line of sight,
  // so an anchor behind a roof slab is not an anchor — and measuring only distance is
  // exactly how a spawn once shipped with seven anchors inside GRAPPLE_RANGE and zero
  // it could fire at. The check is line of sight from standing eye height, which is
  // where a player actually is when they press the key.
  const slabs = solidSlabs();
  for (const s of [...spawnsA, ...spawnsB]) {
    const eye = { x: s.x, y: s.y + PLAYER_EYE_HEIGHT, z: s.z };
    let n = 0;
    for (const a of anchors) {
      const dx = a.pos.x - eye.x;
      const dy = a.pos.y - eye.y;
      const dz = a.pos.z - eye.z;
      if (dx * dx + dy * dy + dz * dz > GRAPPLE_RANGE * GRAPPLE_RANGE) continue;
      if (clearLine(eye, a.pos, slabs)) n++;
    }
    if (n < 2) {
      throw new Error(
        `SKYLINE NINE: spawn (${s.x}, ${s.y}, ${s.z}) can reach only ${n} grapple anchors`,
      );
    }
  }

  // The kill plane has to be below the whole complex, or a fall is survivable and
  // height stops being frightening.
  let lowestWalkable = Infinity;
  for (const b of brushes) {
    if (b.decorative) continue;
    const top = b.pos.y + b.half.y;
    if (top < lowestWalkable) lowestWalkable = top;
  }
  if (KILL_PLANE_Y >= Y_APRON - 12) {
    throw new Error('SKYLINE NINE: kill plane is not far enough below the lowest roof');
  }

  const bounds = computeBounds();
  for (const b of brushes) {
    const [ax, ay, az, bx, by, bz] = aabbOf(b.pos, b.half, b.yaw, 0);
    if (
      ax < bounds.min.x ||
      ay < bounds.min.y ||
      az < bounds.min.z ||
      bx > bounds.max.x ||
      by > bounds.max.y ||
      bz > bounds.max.z
    ) {
      throw new Error(`SKYLINE NINE: brush ${b.id} escapes the arena bounds`);
    }
  }
}

verifyMirror();
verifyPlayable();
verifyOpenSky();
verifySightlines();

/**
 * SKYLINE NINE. Built once at module load. The server's collision world and the
 * client's mesh are both pure functions of this object, so they cannot disagree.
 */
export const ARENA: ArenaData = {
  name: 'SKYLINE NINE',
  brushes,
  ramps,
  anchors,
  spawnsA,
  spawnsB,
  gunSpawnA,
  gunSpawnB,
  centerSpawn,
  killPlaneY: KILL_PLANE_Y,
  bounds: computeBounds(),
};
